import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { TableComponent } from "../../TableComponent";
import useLocalStorageState from "use-local-storage-state";
import { useGetRequest, usePostRequest } from "../../../Utils/hooks/QueryHandler";
import { AuthorizationCard } from "../../../CommonComponents/AuthorizationCard";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconInfoCircle } from "@tabler/icons-react";
import Tooltip from "@mui/material/Tooltip";
import {
  DeleteRequest,
  GetRequest,
  PutRequest,
} from "../../../Utils/hooks/ApiHandler";
import { useGetEssentials } from "../../../Utils/hooks/useGetEssentials";


const schema = z.object({
  roleName: z.string().min(1, "Role Name is required"),
  description: z.string().min(1, "Description is required"),
});


export const RoleManagement = () => {
  // const { register, handleSubmit, reset, setValue } = useForm();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      status: "Active",
    },
  });
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRoleId, setDeleteRoleId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { customerId, navigate, loginUserId, headers, extras } = useGetEssentials()
  // rbac permission object
  const [PERMISSION, _] = useLocalStorageState('permission')

  const [currRoleId, setCurrRoleId] = useState()
  const [isDeleting, setIsDeleting] = useState(false)
  const [isGlobalMasking, setIsGlobalMasking] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    data: rolesData,
    error,
    refetch: refetchRoles,
    isLoading,
    isError,
  } = useGetRequest(
    `/users/Roles/getRoles/${customerId}`, "getRoles", headers, extras, {}
  );


  // role management permission
  const roleGrant = PERMISSION.setting.feature.role_management;

  useEffect(() => {
    if (rolesData?.status === "success") {
      setRoles(rolesData.data);
    } else if (isError) {
      console.error("Error fetching roles:", error);
    }
  }, [rolesData, isError]);

  const { mutate: addRole, isLoading: isSubmitting } = usePostRequest(
    "/users/Roles/addRole",
    false,
    { headers },
    extras
  );


  //  onSubmit function for creating and updationg user role
  const onSubmit = async (data) => {
    const roleName = data.roleName?.trim();
    const description = data.description;
    // Common payload base
    const payloadBase = {
      customer_id: customerId,
      role: roleName,
      description: description,
    };

    try {
      if (currRoleId) {
        setIsUpdating(true);
        const payload = {
          ...payloadBase,
          role_id: currRoleId,
          user_id: loginUserId,
          is_hashed: isGlobalMasking ? "yes" : "no"
        };
        const res = await PutRequest(
          "/users/Roles/updateRole",
          payload,
          { headers },
          extras
        );

        if (!res || res.success === false) {
          toast.error(res?.message || "Update failed");
          return;
        }

        toast.success(res.message || "Role updated successfully");
        await refetchRoles();
        setShowModal(false);
        reset();
        setCurrRoleId(null);
        setIsGlobalMasking(true);

      } else {
        // ADD ROLE FUNCTION
        const payload = {
          ...payloadBase,
          user_id: loginUserId,
          is_hashed: isGlobalMasking ? "yes" : "no",
        };
        addRole(payload, {
          onSuccess: (res) => {
            if (res?.status === "success") {
              // handleSave(res.data?.role_id);
              toast.success(res.message || "Role added successfully");
              // trigger refetch to fetch all roles again for displaying on UI after successfully adding the role
              refetchRoles();
              setShowModal(false);
              reset();
            } else {
              toast.error(res.message || "Failed to add role");
            }
          },
          onError: (err) => {
            toast.error(err?.message || "Add role request failed");
          },
        });
        return;
      }
    } catch (error) {
      toast.error(error?.message || "Error saving role");
    } finally {
      setIsUpdating(false);
    }
  };

  const editingRole = currRoleId
    ? roles.find((r) => r.id === currRoleId)
    : null;

  // handle edit (this fucntion gives me a data of a specific role)
  const handleEdit = async (roleId) => {
    try {
      setCurrRoleId(roleId)
      const res = await GetRequest(
        `/users/Roles/getRoleById/${roleId}`,
        { headers },
        extras
      );
      const role = res?.data?.[0];
      // if not role throw new error
      if (!role) throw new Error("Role not found from getRoleById");
      setValue("roleName", role.role_title);
      setValue("description", role.description);
      setIsGlobalMasking(role.is_hashed === "yes");
      setValue("customer_id", role.customer_id);

      setShowModal(true)
      // await fetchMaskingSettings(roleId);
    } catch (error) {
      // toast error
      toast.error("Error Editing Role !");
    }
  };

  // delete function for delete a user role
  const confirmDelete = async () => {
    setIsDeleting(true)
    try {
      const res = await DeleteRequest(
        `/users/Roles/deleteRole/${deleteRoleId}`,
        { headers },
        extras
      );
      if (res?.status === 'success') {
        setRoles(roles.filter((r) => r.id !== deleteRoleId));
        setShowDeleteModal(false);
        setDeleteRoleId(null);
        setIsDeleting(false)
        // toast success
        toast.success(res.message);
      } else {
        toast.error(res.message)
        setIsDeleting(false)
      }
    } catch (error) {
      //toast error
      toast.error("Delete Failed");
      setIsDeleting(false)
    }
  };

  // Filter roles based on searchTerm (case-insensitive)
  const filteredRoles = roles.filter((role) =>
    role.role_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container">
      <div className="mb-3 d-flex justify-content-between align-items-end">
        <div style={{ lineHeight: "0.8rem" }}>
          <h6
            style={{ fontWeight: "500", color: "black", fontSize: "1.0rem" }}
          >
            Users Permission
          </h6>
          <p className="text-muted mb-0 small" style={{ color: "#c7c7c7" }}>
            You can manage Logo, Receipt, Admission, etc. information LMS system
            from here.
          </p>
        </div>

      </div>
      <hr
        className="mt-2 mb-4"
        style={{
          borderTop: "0.5px solid #6c757d",
          opacity: "0.3",
        }}
      />
      {
        roleGrant?.isView ?
          <div className="">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
              <div className="crm-admin-search">
                <i className="fa-solid fa-magnifying-glass crm-admin-search__icon" aria-hidden="true"></i>
                <input
                  type="search"
                  className="crm-admin-search__input"
                  placeholder="Search roles..."
                  aria-label="Search roles"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <button
                type="button"
                className="crm-admin-control crm-admin-control--primary"
                onClick={() => {
                  if (!roleGrant?.isCreate) {
                    toast.warning("You are not authorized for this action!");
                    return;
                  }
                  reset();
                  setCurrRoleId(null);
                  setShowModal(true);
                  setIsGlobalMasking(true);
                }}
              >
                <i className="bi bi-plus-lg" aria-hidden="true"></i>
                <span>Add role</span>
              </button>
            </div>
            <>
              <TableComponent
                data={filteredRoles}
                columns={["role_title", "description"]}
                columnLabels={{
                  role_title: "Role Name",
                  description: "Description",
                }}
                showDeleteModal={showDeleteModal}
                onDeleteClose={() => setShowDeleteModal(false)}
                onDeleteConfirm={confirmDelete}
                loading={isDeleting || isLoading}
                actions={(row) =>
                  row.is_customized === "1" ? (
                    <>
                      <Button title="Edit" size="sm" variant="outline-primary"
                        className="crm-admin-icon-btn"
                        onClick={() => {
                          if (!roleGrant?.isEdit) {
                            toast.warning("You are not authorized for this action!")
                            return
                          }
                          handleEdit(row.id)
                        }}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button size="sm" title="Delete" variant="outline-danger"
                        className="crm-admin-icon-btn crm-admin-icon-btn--danger"
                        onClick={() => {
                          if (!roleGrant?.isDelete) {
                            toast.warning("You are not authorized for this action!")
                            return
                          }
                          setDeleteRoleId(row.id);
                          setShowDeleteModal(true);
                        }}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                      <Button size="sm" title="Permission setting" variant="outline-secondary"
                        className="crm-admin-icon-btn"
                        onClick={() => {
                          if (!roleGrant?.isEdit) {
                            toast.warning("You are not authorized for this action!")
                            return
                          }
                          navigate('/rolePermissions', {
                            state: {
                              role_id: row.id
                            }
                          })
                        }}
                      >
                        <i className="bi bi-gear"></i>
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button title="Edit" size="sm" variant="outline-primary"
                        className="crm-admin-icon-btn"
                        onClick={() => {
                          if (!roleGrant?.isEdit) {
                            toast.warning("You are not authorized for this action!")
                            return
                          }
                          handleEdit(row.id)
                        }}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button size="sm" title="Permission setting" variant="outline-secondary"
                        className="crm-admin-icon-btn"
                        onClick={() => {
                          if (loginUserId === row.user_id) {
                            toast.warning("You are not authorized for this action!")
                            return
                          }
                          navigate('/rolePermissions', {
                            state: {
                              role_id: row.id
                            }
                          })
                        }}
                      >
                        <i className="bi bi-gear"></i>
                      </Button>
                    </>
                  )
                }
              />
            </>

          </div>
          :
          <AuthorizationCard />
      }
      {/* style={{ background: "rgba(0, 0, 0, 0.55)" }} // The classic black fade */}

      {
        showModal && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ background: "rgba(0, 0, 0, 0.6)" }}
          >
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "480px" }}>
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px" }}>
                <div className="modal-header border-0 pb-0 pt-4 px-4">
                  <h5 className="modal-title fw-semibold text-dark" style={{ letterSpacing: "-0.02em" }}>
                    {currRoleId ? "Edit Role" : "Create New Role"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close shadow-none"
                    style={{ fontSize: "0.8rem" }}
                    onClick={() => {
                      setShowModal(false);
                      reset();
                      setCurrRoleId(null)
                    }}
                  ></button>
                </div>
                <hr className="mx-4 opacity-5" />

                <form className="form-container" onSubmit={handleSubmit(onSubmit)}>
                  <div className="modal-body px-4 pt-2">
                    {/* Role Name */}
                    <div className="mb-3 form-group position-relative">
                      <label className="form-label small fw-medium text-secondary mb-1">
                        Role Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. System Administrator"
                        disabled={
                          editingRole?.role_title?.toLowerCase() === "admin"
                        }
                        className={`form-control shadow-sm border-light-subtle ${errors.roleName ? 'is-invalid' : ''}`}
                        style={{
                          padding: "0.6rem 0.75rem",
                          borderRadius: "8px",
                          fontSize: "0.95rem",
                          backgroundColor:
                            editingRole?.role_title?.toLowerCase() === "admin"
                              ? "#e9ecef"
                              : ""
                        }}
                        {...register("roleName", { required: "Role name is required" })}
                      />
                    </div>

                    {/* Description */}
                    <div className="mb-4 form-group position-relative">
                      <label className="form-label small fw-medium text-secondary mb-1">
                        Description <span className="text-danger">*</span>
                      </label>
                      <textarea
                        placeholder="Briefly describe the responsibilities..."
                        className={`form-control shadow-sm border-light-subtle ${errors.description ? 'is-invalid' : ''}`}
                        rows="2"
                        style={{ borderRadius: "8px", fontSize: "0.95rem", resize: "none" }}
                        {...register("description", { required: "Description is required" })}
                      ></textarea>
                    </div>

                    <div className="d-flex  gap-2">
                      <p className=" fw-bold text-uppercase text-muted mb-3" style={{ fontSize: '10px', letterSpacing: '1px' }}>
                        Number Masking
                      </p>
                      <Tooltip
                        placement="right"
                        arrow
                        title={`Enable number masking for the selected module to protect user privacy.`}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: "0.7rem",
                              backgroundColor: "#7a3a5e",
                              borderRadius: "6px",
                              padding: "6px 10px",
                            },
                          },
                          arrow: { sx: { color: "#7a3a5e" } },
                        }}
                      >
                        <div className="text-muted" style={{ cursor: 'pointer', display: 'flex' }}>
                          <IconInfoCircle size={14} stroke={2} />
                        </div>
                      </Tooltip>
                    </div>

                    <div className="mb-2 form-group position-relative" >

                      <div className="row g-2">
                        <div className="d-flex align-items-center justify-content-between p-3"
                          style={{
                            backgroundColor: "#f8f9fa",
                            borderRadius: "8px",
                            border: "1px solid #f1f3f5"
                          }}>
                          <span className="small fw-medium text-dark">
                            Number Masking
                          </span>

                          <div className=" form-check form-switch p-0 m-0">
                            <input
                              className=" form-check-input shadow-none"
                              type="checkbox"
                              role="switch"
                              checked={isGlobalMasking}
                              onChange={(e) => { setIsGlobalMasking(e.target.checked) }}
                              style={{ width: "2.5em", height: "1.2em", cursor: "pointer" }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer border-0 pb-4 px-4 pt-3">
                    <button
                      type="button"
                      className="btn btn-default-outline fw-medium"
                      disabled={isSubmitting || isUpdating}
                      onClick={() => {
                        setShowModal(false);
                        reset();
                        setCurrRoleId(null)
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-default px-4 py-2 "
                      type="submit"
                      style={{ borderRadius: "8px", fontWeight: "500", fontSize: "0.9rem" }}
                      disabled={isSubmitting || isUpdating}
                    >
                      {(isSubmitting || isUpdating) ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          {currRoleId ? "Saving..." : "Creating..."}
                        </>
                      ) : (
                        currRoleId ? "Save Changes" : "Create Role"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};


